/**
 * SquareSubscribeModal — captures a card-on-file and starts a native Square
 * recurring subscription for the selected medication + tier. Renders after the
 * order is created (recurring tiers only). No card data is typed on GLPFlo.
 */
import { useRef, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { X, Loader2, Repeat, CheckCircle2 } from "lucide-react";
import SquareCardForm from "@/components/SquareCardForm";

const CADENCE_LABEL = { MONTHLY: "every month", QUARTERLY: "every 3 months" };

export default function SquareSubscribeModal({
  open,
  onClose,
  medicationId,
  paymentType,
  orderId,
  tierName,
  amount,
  cadence,
  billingDefaults = {},
  onSuccess,
}) {
  const cardRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [contact, setContact] = useState({
    given_name: billingDefaults.given_name || "",
    family_name: billingDefaults.family_name || "",
    email: billingDefaults.email || "",
    postal_code: billingDefaults.postal_code || "",
  });

  if (!open) return null;

  const set = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  const start = async () => {
    if (!contact.given_name.trim() || !contact.family_name.trim()) {
      toast.error("Please enter the cardholder's first and last name."); return;
    }
    setSubmitting(true);
    try {
      const nonce = await cardRef.current.tokenize({
        givenName: contact.given_name,
        familyName: contact.family_name,
        email: contact.email || undefined,
        postalCode: contact.postal_code || undefined,
        countryCode: "US",
      });
      const { data } = await API.post("/payments/subscribe", {
        nonce,
        medication_id: medicationId,
        payment_type: paymentType,
        order_id: orderId || null,
        billing_contact: {
          given_name: contact.given_name,
          family_name: contact.family_name,
          email: contact.email || null,
          postal_code: contact.postal_code || null,
        },
      });
      setDone(data);
      toast.success("Subscription started — your card is securely on file.");
      if (onSuccess) onSuccess(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not start the subscription.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      data-testid="square-subscribe-modal"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
              <Repeat className="w-4 h-4 text-sky-600" />
            </div>
            <h3 className="font-semibold text-slate-900">Start subscription</h3>
          </div>
          {!submitting && (
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="subscribe-modal-close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {done ? (
          <div className="p-6 text-center" data-testid="subscribe-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h4 className="font-semibold text-slate-900 mb-1">You&apos;re subscribed!</h4>
            <p className="text-sm text-slate-600">
              {done.tier_name} · ${Number(done.amount).toFixed(2)} {CADENCE_LABEL[done.cadence] || ""}.
              Your card is securely saved with Square for renewals.
            </p>
            <Button className="mt-5 w-full rounded-full bg-purple-600 hover:bg-purple-700 text-white" onClick={onClose} data-testid="subscribe-done-btn">
              Continue
            </Button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="rounded-xl bg-sky-50 border border-sky-100 p-3 text-sm">
              <p className="font-medium text-slate-800">{tierName}</p>
              <p className="text-slate-600">
                ${Number(amount).toFixed(2)} billed {CADENCE_LABEL[cadence] || "recurring"}. Cancel anytime.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">First name</label>
                <Input value={contact.given_name} onChange={set("given_name")} data-testid="subscribe-first-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Last name</label>
                <Input value={contact.family_name} onChange={set("family_name")} data-testid="subscribe-last-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Email</label>
                <Input value={contact.email} onChange={set("email")} data-testid="subscribe-email" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">ZIP</label>
                <Input value={contact.postal_code} onChange={set("postal_code")} data-testid="subscribe-zip" />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Card</label>
              <SquareCardForm ref={cardRef} />
            </div>

            <Button
              onClick={start}
              disabled={submitting}
              className="w-full rounded-full bg-sky-600 hover:bg-sky-700 text-white py-6"
              data-testid="subscribe-start-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> : `Subscribe · $${Number(amount).toFixed(2)}`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
